import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTimezoneConverter } from './useTimezoneConverter';
import { fetchStoreDetailAPI } from '@/api/stores';
import { useAppContext } from '@/provider/contexts';
import { StoreInfo, StoreSettings } from '@/types';

export const useStoreDetails = ({ autoFetch = false } = {}) => {
    const { manageStoreId } = useAppContext();

    const [storeLoading, setStoreLoading] = useState(false);
    const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
    const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
    const [storeTimezone, setStoreTimezone] = useState<string | null>(null);
    const [storeDetailsError, setStoreDetailsError] = useState<string | null>(null);
    const [isStoreInvalid, setIsStoreInvalid] = useState(false);

    const requestControllerRef = useRef<AbortController | null>(null);
    const requestSequenceRef = useRef(0);
    const latestStoreIdRef = useRef(manageStoreId);

    const timezoneUtils = useTimezoneConverter(storeTimezone);

    useEffect(() => {
        latestStoreIdRef.current = manageStoreId;
        requestSequenceRef.current += 1;
    }, [manageStoreId]);

    const resetStoreDetails = useCallback(() => {
        setStoreSettings(null);
        setStoreTimezone(null);
        setStoreInfo(null);
    }, []);

    const fetchStoreDetails = useCallback(
        async (targetStoreId = manageStoreId) => {
            requestControllerRef.current?.abort();

            if (!targetStoreId) {
                resetStoreDetails();
                setIsStoreInvalid(false);
                setStoreDetailsError(null);
                setStoreLoading(false);
                return null;
            }

            const controller = new AbortController();
            const requestSequence = ++requestSequenceRef.current;

            requestControllerRef.current = controller;
            setStoreLoading(true);
            setStoreDetailsError(null);

            const isStaleRequest = () =>
                controller.signal.aborted ||
                requestSequence !== requestSequenceRef.current ||
                latestStoreIdRef.current !== targetStoreId;

            try {
                const {
                    data: { data, isSuccess },
                } = await fetchStoreDetailAPI(targetStoreId, {
                    signal: controller.signal,
                });

                if (isStaleRequest()) {
                    return null;
                }

                if (!isSuccess) {
                    resetStoreDetails();
                    setIsStoreInvalid(true);
                    setStoreDetailsError('This store is no longer available. It may have been deleted or deactivated.');
                    return null;
                }

                const settings = data.store_settings || null;

                setStoreSettings(settings);
                setStoreTimezone(settings?.store_timezone || null);
                setStoreInfo(data.store_info || null);
                setIsStoreInvalid(false);
                setStoreDetailsError(null);

                return data;
            } catch (error: unknown) {
                if (axios.isCancel(error) || isStaleRequest()) {
                    return null;
                }

                setStoreDetailsError(error instanceof Error ? error.message : 'Failed to load store details.');

                return null;
            } finally {
                if (requestControllerRef.current === controller && requestSequence === requestSequenceRef.current) {
                    requestControllerRef.current = null;
                    setStoreLoading(false);
                }
            }
        },
        [manageStoreId, resetStoreDetails],
    );

    useEffect(() => {
        if (autoFetch && manageStoreId) {
            void fetchStoreDetails(manageStoreId);
        }

        return () => {
            requestControllerRef.current?.abort();
        };
    }, [autoFetch, manageStoreId, fetchStoreDetails]);

    return {
        storeLoading,
        storeInfo,
        storeSettings,
        storeTimezone,
        storeDetailsError,
        isStoreInvalid,
        fetchStoreDetails,
        ...timezoneUtils,
    };
};
